import React from 'react'

import { SigningState } from '../../utils/signing-state'

// Derived from SigningState so the icon's accepted variants can never drift
// from the state helper: everything except 'none' (existing pending/attachment
// icons) and 'executed' (existing green icons).
export type SigningProgressVariant = Exclude<SigningState, 'none' | 'executed'>

interface SigningProgressIconProps {
  variant: SigningProgressVariant
  // At least one signature arrived offline (adminSignedFor) — render the
  // paperclip composition, mirroring how the executed state distinguishes
  // all-signatures-complete.svg (offline) from all-lendos-... (online).
  isOffline?: boolean
  onClick?: () => void
  style?: React.CSSProperties
  // Override for columns with an established test id (e.g. the FM column's
  // fm-document-generate-button); defaults to signing-progress-icon-<variant>.
  dataTestId?: string
}

const AMBER = '#E8A33D'
const BLUE = '#2F80ED'
const LETTER_COLOR = '#3D2A00'

// ── Online composition — copied verbatim from
// app/public/DrawerIcons/all-lendos-signatures-complete.svg so the AA states
// share an identical glyph. Badge centered at (19.56, 25.56).
const DOC_OUTLINE =
  'M32.5 17.2852V32.5C32.5 33.4946 32.1049 34.4484 31.4017 35.1517C30.6984 35.8549 29.7446 36.25 28.75 36.25H11.25C10.2554 36.25 9.30161 35.8549 8.59835 35.1517C7.89509 34.4484 7.5 33.4946 7.5 32.5V7.5C7.5 6.50544 7.89509 5.55161 8.59835 4.84835C9.30161 4.14509 10.2554 3.75 11.25 3.75H18.9648C19.6277 3.7501 20.2633 4.01341 20.732 4.48203L31.768 15.518C32.2366 15.9867 32.4999 16.6223 32.5 17.2852Z'
const DOC_FOLD =
  'M20 4.375V13.75C20 14.413 20.2634 15.0489 20.7322 15.5178C21.2011 15.9866 21.837 16.25 22.5 16.25H31.875'
const CHECK_BADGE =
  'M19.56 19C15.9429 19 13 21.9429 13 25.56C13 29.1771 15.9429 32.12 19.56 32.12C23.1771 32.12 26.12 29.1771 26.12 25.56C26.12 21.9429 23.1771 19 19.56 19ZM22.974 23.3615L18.7353 28.4076C18.6888 28.463 18.6309 28.5077 18.5656 28.5388C18.5003 28.5699 18.4291 28.5865 18.3568 28.5877H18.3483C18.2776 28.5877 18.2076 28.5728 18.143 28.544C18.0784 28.5152 18.0206 28.4731 17.9733 28.4205L16.1567 26.4021C16.1105 26.3531 16.0747 26.2955 16.0511 26.2325C16.0276 26.1695 16.0169 26.1024 16.0197 26.0352C16.0224 25.968 16.0386 25.9021 16.0672 25.8412C16.0958 25.7803 16.1363 25.7258 16.1863 25.6808C16.2363 25.6358 16.2948 25.6013 16.3583 25.5792C16.4218 25.5572 16.4891 25.548 16.5562 25.5523C16.6233 25.5567 16.6889 25.5743 16.7491 25.6044C16.8093 25.6344 16.8629 25.6761 16.9067 25.7272L18.335 27.3142L22.2013 22.7124C22.2881 22.6121 22.4108 22.55 22.5429 22.5395C22.6751 22.529 22.8061 22.5708 22.9076 22.6561C23.0091 22.7414 23.073 22.8632 23.0854 22.9952C23.0978 23.1271 23.0578 23.2587 22.974 23.3615Z'

// ── Offline composition — copied verbatim from
// app/public/DrawerIcons/all-signatures-complete.svg: document shifted right,
// paperclip on the left, badge centered at (22.56, 27.56).
const OFFLINE_DOC_OUTLINE =
  'M16.25 5H21.4648C22.1277 5.0001 22.7633 5.26341 23.232 5.73203L34.268 16.768C34.7366 17.2367 34.9999 17.8723 35 18.5352V33.75C35 34.7446 34.6049 35.6984 33.9017 36.4017C33.1984 37.1049 32.2446 37.5 31.25 37.5H15C14.0054 37.5 13.0516 37.1049 12.3483 36.4017C11.6451 35.6984 11.25 34.7446 11.25 33.75V23.75'
const OFFLINE_DOC_FOLD =
  'M22.5 5.625V15C22.5 15.663 22.7634 16.2989 23.2322 16.7678C23.7011 17.2366 24.337 17.5 25 17.5H34.375'
const PAPERCLIP =
  'M12.5 6.25V18.125C12.5033 18.3721 12.457 18.6174 12.3639 18.8464C12.2708 19.0753 12.1328 19.2833 11.9581 19.4581C11.7833 19.6328 11.5753 19.7708 11.3464 19.8639C11.1174 19.957 10.8721 20.0033 10.625 20C9.6875 20 8.75 19.2891 8.75 18.125V6.875C8.75 4.48516 10.0445 2.5 12.5 2.5C14.9555 2.5 16.25 4.4375 16.25 6.82656V17.6664C16.25 21.0258 14.0766 23.75 10.625 23.75C7.17344 23.75 5 21.0266 5 17.6664V11.25'
const OFFLINE_CHECK_BADGE =
  'M22.56 21C18.9429 21 16 23.9429 16 27.56C16 31.1771 18.9429 34.12 22.56 34.12C26.1771 34.12 29.12 31.1771 29.12 27.56C29.12 23.9429 26.1771 21 22.56 21ZM25.974 25.3615L21.7353 30.4076C21.6888 30.463 21.6309 30.5077 21.5656 30.5388C21.5003 30.5699 21.4291 30.5865 21.3568 30.5877H21.3483C21.2776 30.5877 21.2076 30.5728 21.143 30.544C21.0784 30.5152 21.0206 30.4731 20.9733 30.4205L19.1567 28.4021C19.1105 28.3531 19.0747 28.2955 19.0511 28.2325C19.0276 28.1695 19.0169 28.1024 19.0197 28.0352C19.0224 27.968 19.0386 27.9021 19.0672 27.8412C19.0958 27.7803 19.1363 27.7258 19.1863 27.6808C19.2363 27.6358 19.2948 27.6013 19.3583 27.5792C19.4218 27.5572 19.4891 27.548 19.5562 27.5523C19.6233 27.5567 19.6889 27.5743 19.7491 27.6044C19.8093 27.6344 19.8629 27.6761 19.9067 27.7272L21.335 29.3142L25.2013 24.7124C25.2881 24.6121 25.4108 24.55 25.5429 24.5395C25.6751 24.529 25.8061 24.5708 25.9076 24.6561C26.0091 24.7414 26.073 24.8632 26.0854 24.9952C26.0978 25.1271 26.0578 25.2587 25.974 25.3615Z'

// Signing-progress states of the Documents-tab icons (C / AA / FM) that are
// not covered by the existing DrawerIcons assets: amber S (Seller signed,
// awaiting Buyer), amber B (Buyer signed, awaiting Seller), blue check (both
// counterparties signed, awaiting Agent/Reviewer — AA only; C / FM go green
// once both counterparties sign). Each comes in an online and an offline
// (paperclip) composition.
export const SigningProgressIcon: React.FC<SigningProgressIconProps> = ({
  variant,
  isOffline = false,
  onClick,
  style,
  dataTestId,
}) => {
  const badgeCx = isOffline ? 22.56 : 19.56
  const badgeCy = isOffline ? 27.56 : 25.56
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={40}
      height={40}
      viewBox="0 0 40 40"
      fill="none"
      onClick={onClick}
      style={style}
      data-test-id={dataTestId ?? `signing-progress-icon-${variant}${isOffline ? '-offline' : ''}`}
    >
      {isOffline ? (
        <>
          <path d={OFFLINE_DOC_OUTLINE} stroke="black" strokeLinecap="round" strokeLinejoin="round" />
          <path d={OFFLINE_DOC_FOLD} stroke="black" strokeLinecap="round" strokeLinejoin="round" />
          <path d={PAPERCLIP} stroke="black" strokeMiterlimit={10} strokeLinecap="round" />
        </>
      ) : (
        <>
          <path d={DOC_OUTLINE} stroke="black" strokeLinejoin="round" />
          <path d={DOC_FOLD} stroke="black" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}
      {variant === 'both' ? (
        <path d={isOffline ? OFFLINE_CHECK_BADGE : CHECK_BADGE} fill={BLUE} />
      ) : (
        <>
          <circle cx={badgeCx} cy={badgeCy} r={6.56} fill={AMBER} />
          <text
            x={badgeCx}
            y={badgeCy}
            textAnchor="middle"
            // Cap-height centring instead of dominant-baseline="central":
            // browsers derive `central` from font metrics, which sits the glyph
            // low for this font, and the error scales up with the icon. Placing
            // the alphabetic baseline half a cap-height (~0.35em) below the
            // circle's centre puts the letter in the middle at any size.
            dy="0.35em"
            fontSize={9}
            fontWeight={700}
            fontFamily="Plus Jakarta Sans, sans-serif"
            fill={LETTER_COLOR}
          >
            {variant === 'seller' ? 'S' : 'B'}
          </text>
        </>
      )}
    </svg>
  )
}
